<template>
  <table class="reception-list">
    <thead>
      <tr>
        <th class="rcp-time">受付{{ dateType === 2 ? '日時' : '時間' }}</th>
        <th class="operator">媒体／入力者</th>
        <th class="status">ステータス</th>
        <th class="rcp-date">予約日時</th>
        <th class="subscriber">予約者</th>
        <th class="table">テーブル</th>
      </tr>
    </thead>
    <VirtualScroller
      class="scroller"
      v-infinite-scroll="handleScrollInfinite"
      infinite-scroll-disabled="isGetHistoryByConditionInfinityDisabled"
      infinite-scroll-distance="800"
      :items="historyList"
      :heightCache="heights"
      min-item-height="89"
    >
      <tbody v-if="isNoHistory" slot="before-container">
        <tr class="no-data">
          <td>
            <CalendarIcon blank clock />
            <p>該当する受付履歴はありません。</p>
          </td>
        </tr>
      </tbody>
      <template v-if="isActive" slot-scope="{ item, itemIndex }">
        <CellMeasurer :heights="heights" :index="itemIndex" class="reception-item">
          <tr v-if="isVisibleIndex(itemIndex)" class="reception-date">
            <td v-if="dateType === 1">{{ formatDate(item.receiptDatetime) }} 受付</td>
            <td v-else-if="dateType === 2">{{ formatDate(item.reserveDatetime.date) }} 予約</td>
          </tr>
          <tr class="item" @click="goDetail(item.id)">
            <td class="tb-history">
              <div class="rcp-time">
                <span>
                  {{ receiptDate(item.receiptDatetime) }}
                </span>
              </div>
              <div class="operator separate">
                <span class="top">{{ mediaIds[item.media] }}</span>
                <span class="bottom">{{ item.inputBy || 'ステータス変更' }}</span>
              </div>
              <div class="status">
                <ReceptionStatus :status="item.status" />
              </div>
              <div class="rcp-date separate">
                <span class="top">
                  {{ formatDate(item.reserveDatetime.date) }}
                  {{ item.reserveDatetime.time }} 〜
                </span>
                <span v-if="reserveNumDisplay(item)" class="bottom">{{ reserveNumDisplay(item) }}</span>
              </div>
              <div class="subscriber">
                <span class="name" :class="{ suffix: !isBlock(item.type) }">{{
                  toEllipsis(reserveName(item), 20)
                }}</span>
                <span v-if="isShowRuby(item)" class="ruby">
                  {{ toEllipsis(ruby(item.customer), 20) }}
                </span>
              </div>
              <div class="table">
                <template v-if="item.tables">
                  <span v-for="table in item.tables" :key="`${table.name}-${table.id}`">{{ table.name }}</span>
                </template>
                <span v-else>未指定</span>
              </div>
            </td>
            <td class="sp-history">
              <div class="reserve-info">
                <div class="status">
                  <ReceptionStatus :status="item.status" />
                </div>
                <div class="receipt-time">{{ receiptDate(item.receiptDatetime) }} 受付</div>
                <div class="input-by">{{ item.inputBy || 'ステータス変更' }}</div>
              </div>
              <div class="subscriber">
                <span class="name">{{ reserveName(item) }}</span>
                <span v-if="!isBlock(item.type)" class="name-suffix">様</span>
              </div>
              <div v-if="isShowRuby(item)" class="ruby">{{ ruby(item.customer) }}</div>
              <div class="rcp-date">
                <CalendarIcon />
                <span class="date-time">
                  {{ formatDate(item.reserveDatetime.date) }}
                  {{ item.reserveDatetime.time }} 〜
                </span>
              </div>
              <div v-if="reserveNumDisplay(item)" class="people">
                <AddPeopleIcon />
                <span>{{ reserveNumDisplay(item) }}</span>
              </div>
              <div class="table">
                <TableIcon />
                <template v-if="item.tables">
                  <span class="table-name">
                    {{ item.tables.map(t => t.name).join(',') }}
                  </span>
                  <span class="table-count" v-if="item.tables.length > 1">全{{ item.tables.length }}</span>
                </template>
                <span v-else class="table-name">未指定</span>
              </div>
            </td>
          </tr>
        </CellMeasurer>
      </template>
      <tr v-if="busy || loadInitial" slot="after-container" :class="{ 'no-data': loadInitial }">
        <LoadingIndicator :small="!loadInitial" />
      </tr>
    </VirtualScroller>
  </table>
</template>

<script>
import InfiniteScroll from 'vue-infinite-scroll'
import { format } from 'date-fns'
import { mapState } from 'vuex'
import { formatDate } from '@/utils/date'
import { toEllipsis } from '@/utils/string'
import { reserveName, reserveNumDisplay } from '@/utils/reserve'
import { isBlock } from '@/resources/reservation/typeMap'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import VirtualScroller, { CellMeasurer } from '@/components/molecules/VirtualScroller'
import ReceptionStatus from '@/components/atoms/ReceptionStatus'
import AddPeopleIcon from '@/components/icons/addPeople'
import CalendarIcon from '@/components/icons/CalendarIcon'
import TableIcon from '@/components/icons/table'

export default {
  name: 'HistoryList',
  components: {
    LoadingIndicator,
    VirtualScroller,
    CellMeasurer,
    ReceptionStatus,
    AddPeopleIcon,
    CalendarIcon,
    TableIcon,
  },
  directives: {
    InfiniteScroll,
  },
  props: {
    historyList: {
      type: Array,
    },
    busy: {
      type: Boolean,
    },
    ended: {
      type: Boolean,
    },
    heights: {
      type: Array,
    },
    dateType: {
      type: Number,
    },
  },
  computed: {
    ...mapState({
      isSP: state => state.app.isSP,
      mediaIds: state => state.medias.byId,
    }),
    isGetHistoryByConditionInfinityDisabled() {
      return this.busy || this.ended || !this.isActive
    },
    isNoHistory() {
      return this.ended && !Object.keys(this.historyList).length
    },
    isActive() {
      return Object.keys(this.historyList).length > 0
    },
    loadInitial() {
      return this.busy && !this.ended && !Object.keys(this.historyList).length
    },
  },
  methods: {
    toEllipsis,
    reserveName,
    format,
    reserveNumDisplay,
    isBlock,
    formatDate(date) {
      return formatDate(date, 'YYYY/M/D (dd)')
    },
    ruby({ lastNameKana: last, firstNameKana: first }) {
      return (last && first && `${last} ${first}`) || last || first
    },
    handleScrollInfinite() {
      this.$emit('scrollInfinite')
    },
    isVisibleIndex(index) {
      if (index === 0) return true

      const current = this.historyList[index]
      const before = this.historyList[index - 1]
      return this.dateType === 1
        ? formatDate(current.receiptDatetime) !== formatDate(before.receiptDatetime)
        : current.reserveDatetime.date !== before.reserveDatetime.date
    },
    goDetail(id) {
      if (this.isSP) {
        this.$router.push(`/sp/reserve/${id}`)
      } else {
        location.assign(`/reservation/history/detail?reservation_id=${id}`)
      }
    },
    isShowRuby(item) {
      const { customer } = item
      return (
        !item.reserveName &&
        (customer.lastName || customer.firstName) &&
        (customer.lastNameKana || customer.firstNameKana)
      )
    },
    receiptDate(date) {
      return format(date, `${this.dateType === 2 ? 'YYYY/MM/DD ' : ''}HH:mm`)
    },
  },
}
</script>

<style lang="postcss" scoped>
.reception-list {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  overflow-y: auto;
  width: 100%;

  thead,
  tbody {
    display: block;
    width: 100%;
  }

  tbody {
    height: 100%;
    overflow-y: scroll;
  }

  tr {
    border-top: 1px solid var(--border-color);
    display: flex;
    width: 100%;

    &.reception-date {
      border-top: none;

      & + tr {
        border-top: none;
      }
    }
  }

  th,
  td {
    box-sizing: border-box;
  }

  th {
    background-color: var(--very-dark-gray);
    border-right: 1px solid var(--white);
    color: var(--white);
    font-size: 13px;
    height: 32px;
    line-height: 32px;
    text-align: center;

    &:last-child {
      border-right: none;
    }

    &.people,
    &.name {
      &::after {
        display: none;
      }
    }
  }

  td {
    align-items: stretch;
    background-color: var(--white);
    color: var(--text-color);
    display: flex;
    position: relative;
    text-decoration: none;
    width: 100%;

    > div {
      align-content: center;
      align-items: center;
      box-sizing: border-box;
      display: flex;
      flex-wrap: wrap;
      min-height: 88px;
      padding: 16px 0;
      position: relative;

      &::after {
        background-image: linear-gradient(
          180deg,
          var(--dark-gray),
          var(--dark-gray) 1px,
          transparent 0,
          transparent 7px
        );
        background-position: right;
        background-repeat: repeat-y;
        background-size: 1px 8px;
        content: '';
        height: 100%;
        position: absolute;
        right: 0;
        top: 0;
        width: 1px;
      }

      &.operator {
        &::after {
          background-image: linear-gradient(180deg, var(--border-color), var(--border-color));
        }
      }

      &:last-child {
        &::after {
          display: none;
        }
      }
    }
  }

  .rcp-time {
    flex-basis: 120px;
  }

  .operator {
    flex-basis: 160px;
  }

  .status {
    flex-basis: 136px;
  }

  .rcp-date {
    flex-basis: 232px;
  }

  .subscriber {
    flex-basis: 200px;
    flex-grow: 1;
  }

  .table {
    flex-basis: 216px;
    flex-grow: 1;
  }

  .no-data {
    border-top: none;
    height: 100%;
    position: absolute;
    top: 0;

    td {
      align-items: center;
      background-color: var(--very-light-gray-4);
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: center;

      p {
        margin-top: 10px;
      }

      svg {
        fill: var(--light-gray);
        height: 60px;
        width: 60px;
      }
    }
  }

  .sp-history {
    display: none;
  }
}

.items {
  .item {
    cursor: pointer;
  }

  .rcp-time,
  .operator {
    background-color: var(--mostly-white);
    justify-content: center;

    span {
      font-size: 13px;
      font-weight: 600;
      text-align: center;
      word-break: break-word;
    }
  }

  .status {
    justify-content: center;
  }

  .separate {
    span {
      line-height: 1;
      text-align: center;
      width: 100%;

      &.bottom {
        margin-top: 8px;
        padding-top: 8px;
        position: relative;
        &::after {
          background: var(--border-color);
          content: '';
          height: 1px;
          left: 16px;
          position: absolute;
          top: 0;
          width: calc(100% - 32px);
        }
      }
    }
  }

  .subscriber,
  .table {
    span {
      margin: 0 16px;
      width: 100%;
    }
  }

  .subscriber {
    .name {
      font-weight: 600;

      &.suffix::after {
        content: '様';
        font-size: 13px;
        font-weight: normal;
        margin-left: 8px;
      }
    }

    .ruby {
      font-size: 11px;
    }
  }

  .table {
    min-width: 0;

    span {
      color: var(--brand-color);
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .reception-date {
    td {
      align-items: center;
      background-color: var(--very-light-gray-2);
      font-weight: 600;
      height: 40px;
      padding-left: 16px;
      width: 100%;
    }
  }
}

.responsive {
  @media screen and (max-width: 1020px) {
    .reception-list {
      thead {
        display: none;
      }

      .reception-date td {
        border-top: 1px solid var(--border-color);
        height: 32px;
      }

      .rcp-time,
      .operator,
      .status,
      .rcp-date,
      .subscriber,
      .table {
        flex-basis: 0;
        flex-grow: 0;
      }

      .rcp-date,
      .people,
      .table {
        display: grid;
        grid-gap: 0 8px;
        grid-template-columns: 16px 1fr auto;

        span {
          line-height: 1.1;
        }
      }
    }

    .reception-item:first-child {
      .reception-date td {
        border-top: none;
      }
    }

    .tb-history {
      display: none;
    }

    .sp-history.sp-history {
      display: grid;
      grid-gap: 8px 0;
      padding: 16px;

      div {
        flex-wrap: initial;
        min-height: 0;
        padding: 0;

        &::after {
          background-image: none;
        }
      }

      svg {
        fill: var(--very-dark-gray-2);
        height: 16px;
        width: 16px;
      }

      .table {
        .table-name {
          color: var(--text-color);
          font-size: 15px;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          width: auto;
        }

        .table-count {
          border: 1px solid var(--dark-gray-1);
          box-sizing: border-box;
          color: var(--very-dark-gray-2);
          font-size: 10px;
          height: 16px;
          line-height: 1.4;
          margin: 0;
          padding: 0 4px;
        }
      }

      .reserve-info {
        display: grid;
        grid-gap: 0 8px;
        grid-template-columns: auto auto 1fr;

        .input-by {
          justify-self: flex-end;
        }

        .receipt-time,
        .input-by {
          font-size: 12px;
        }
      }

      .subscriber {
        display: grid;
        grid-auto-flow: column;
        grid-gap: 0 4px;
        height: 24px;
        justify-content: flex-start;
        overflow: hidden;

        .name {
          font-size: 16px;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          width: auto;

          &::after {
            display: none;
          }
        }

        .name-suffix {
          font-size: 13px;
          font-weight: normal;
          margin: 0;
        }
      }

      .rcp-date {
        .date-time {
          padding: 0;
          text-align: left;
        }
      }
    }

    .ruby {
      display: block;
      font-size: 12px;
      height: 16px;
      margin-top: -8px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
